import React, { useEffect, useRef, useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper,
  Card, 
  CardContent,
  AppBar,
  Toolbar,
  useTheme as useMuiTheme,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Tabs,
  Tab,
  Divider,
  Button,
  Chip,
  CircularProgress
} from '@mui/material';
import { AgCharts } from 'ag-charts-react';
import NotificationTicker from './notifications/NotificationTicker';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ErrorIcon from '@mui/icons-material/Error';
import { Responsive, WidthProvider } from 'react-grid-layout';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import SettingsIcon from '@mui/icons-material/Settings';
import TimelineIcon from '@mui/icons-material/Timeline';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useNavigate } from 'react-router-dom';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import axios from 'axios';
import ConversationPanel from './BusinessInsights/ConversationPanel';
import { useTenant } from '../context/TenantContext';
import { tenantManagementService } from '../services/tenantManagement.service';
import { useTheme } from '../theme/ThemeProvider';
import { reportsService } from '../services/reports.service';
import { useFeatureFlags } from '../context/FeatureFlagContext';
import { FEATURE_CODES } from '../context/FeatureFlagContext';
import PowerIcon from '@mui/icons-material/Power';
import TimerIcon from '@mui/icons-material/Timer';
import EventIcon from '@mui/icons-material/Event';
import SpeedIcon from '@mui/icons-material/Speed';
import CheckCircle from '@mui/icons-material/CheckCircle';

const ResponsiveGridLayout = WidthProvider(Responsive);

// API base URL with v1
//const API_BASE_URL = '/api/v1/business-insights';

const MetricBox = ({ title, value, trend, icon: Icon, trendValue, unit, description }) => {
  const theme = useMuiTheme();
  
  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUpIcon sx={{ color: theme.palette.success.main }} />;
    if (trend === 'down') return <TrendingDownIcon sx={{ color: theme.palette.error.main }} />;
    return null;
  };

  const getTrendColor = () => {
    if (trend === 'up') return theme.palette.success.main;
    if (trend === 'down') return theme.palette.error.main;
    return theme.palette.text.secondary;
  };

  return (
    <Paper 
      elevation={2}
      sx={{ 
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Icon sx={{ mr: 1, color: theme.palette.primary.main }} />
        <Typography variant="h6" color="text.secondary">
          {title}
        </Typography>
      </Box>
      
      <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
          {value}
        </Typography>
        {unit && (
          <Typography variant="body1" color="text.secondary" sx={{ ml: 1 }}>
            {unit}
          </Typography>
        )}
        {getTrendIcon()}
      </Box>
      
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {description}
        </Typography>
      )}
      
      <Typography 
        variant="body2" 
        sx={{ 
          color: getTrendColor(),
          display: 'flex',
          alignItems: 'center'
        }}
      >
        {trendValue}
      </Typography>
    </Paper>
  );
};

const Dashboard = () => {
  // Always use MUI's theme as a base
  const muiTheme = useMuiTheme();
  
  // Use our custom theme hook, which will fall back to MUI theme if needed
  const { theme = muiTheme, themeConfig = {} } = useTheme();
  
  const { tenantId } = useTenant();
  const [tenantInfo, setTenantInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [layouts, setLayouts] = useState({
    lg: [
      { i: 'serviceResponse', x: 0, y: 0, w: 6, h: 8 },
      { i: 'equipmentHealth', x: 6, y: 0, w: 6, h: 8 },
      { i: 'weeklyServiceUptime', x: 0, y: 8, w: 6, h: 8 },
      { i: 'serviceResolution', x: 6, y: 8, w: 6, h: 8 }
    ]
  });
  
  // Update the initial visibleCharts state to only show the four industrial service charts
  const [visibleCharts, setVisibleCharts] = useState([
    'serviceResponse',
    'equipmentHealth',
    'weeklyServiceUptime',
    'serviceResolution'
  ]);
  
  const [activeTab, setActiveTab] = useState(0);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedReportData, setSelectedReportData] = useState(null);
  
  // Create refs for each chart
  const revenueDeptChartRef = useRef(null);
  const inventoryChartRef = useRef(null);
  const planningChartRef = useRef(null);
  const ordersChartRef = useRef(null);

  // Get feature flags
  const { isFeatureEnabled } = useFeatureFlags();
  const isLLMChatEnabled = isFeatureEnabled(FEATURE_CODES.INSIGHTS_LLMCHAT);

  // Fetch reports for the assistant
  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        const response = await reportsService.getBusinessInsightsReports({ limit: 5 });
        setReports(response);
        
        // Select the most recent report by default
        if (response.length > 0) {
          setSelectedReport(response[0].id);
        }
      } catch (error) {
        console.error('Error fetching reports:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, []);

  // Replace the API call without fallbacks
  useEffect(() => {
    const fetchReportData = async () => {
      try {
        setLoading(true);
        
        // First, get the list of reports to find the latest one
        const reportsResponse = await reportsService.getBusinessInsightsReports({ limit: 1 });
        
        if (reportsResponse && reportsResponse.length > 0) {
          const latestReport = reportsResponse[0];
          setSelectedReport(latestReport.id);
          
          // Then fetch the details of the latest report
          const reportDetailsResponse = await reportsService.getBusinessInsightsReportById(latestReport.id);
          
          if (reportDetailsResponse) {
            setSelectedReportData(reportDetailsResponse);
          }
        }
      } catch (error) {
        console.error('Error fetching report data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, []);

  useEffect(() => {
    const fetchTenantInfo = async () => {
      try {
        setLoading(true);
        if (tenantId) {
          const response = await tenantManagementService.getTenantById(tenantId);
          setTenantInfo(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch tenant info:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTenantInfo();
  }, [tenantId]);
  
  // Get custom properties from theme with fallbacks
  const appName = themeConfig?.custom?.appName || 'Supply Chain Management';
  const dashboardLayout = themeConfig?.custom?.dashboardLayout || 'standard';
  
  // Revenue by Department Chart
  const serviceResponseChartOptions = {
    title: {
      text: 'Service Response Time',
    },
    subtitle: {
      text: 'Average response time by priority level'
    },
    data: [
      { priority: 'Critical', responseTime: 1.2, target: 2 },
      { priority: 'High', responseTime: 4.5, target: 8 },
      { priority: 'Medium', responseTime: 12.3, target: 24 },
      { priority: 'Low', responseTime: 36.7, target: 48 }
    ],
    series: [{
      type: 'bar',
      xKey: 'priority',
      yKey: 'responseTime',
      yName: 'Actual',
      fill: theme.palette.primary.main,
      strokeWidth: 0,
      tooltip: {
        renderer: (params) => {
          return {
            content: `${params.datum.responseTime} hours`
          };
        }
      }
    }, {
      type: 'bar',
      xKey: 'priority',
      yKey: 'target',
      yName: 'Target',
      fill: theme.palette.grey[300],
      strokeWidth: 0,
      tooltip: {
        renderer: (params) => {
          return {
            content: `Target: ${params.datum.target} hours`
          };
        }
      }
    }]
  };

  // Inventory Chart Options
  const equipmentHealthChartOptions = {
    title: {
      text: 'Equipment Health Status',
    },
    subtitle: {
      text: 'Current distribution of equipment health'
    },
    data: [
      { status: 'Optimal', value: 65, percentage: '65%' },
      { status: 'Warning', value: 25, percentage: '25%' },
      { status: 'Critical', value: 10, percentage: '10%' }
    ],
    series: [{
      type: 'pie',
      angleKey: 'value',
      sectorLabelKey: 'percentage',
      calloutLabelKey: 'status',
      fill: [
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main
      ],
      tooltip: {
        renderer: (params) => {
          return {
            content: `${params.datum.value}% (${params.datum.status})`
          };
        }
      }
    }]
  };

  // Replace maintenanceScheduleChartOptions with weeklyServiceUptimeChartOptions
  const weeklyServiceUptimeChartOptions = {
    title: {
      text: 'Weekly Service Uptime',
    },
    subtitle: {
      text: 'System uptime trend over the last 6 weeks'
    },
    data: [
      { week: 'W1', uptime: 99.2, target: 99 },
      { week: 'W2', uptime: 98.5, target: 99 },
      { week: 'W3', uptime: 99.7, target: 99 },
      { week: 'W4', uptime: 98.9, target: 99 },
      { week: 'W5', uptime: 99.1, target: 99 },
      { week: 'W6', uptime: 99.2, target: 99 }
    ],
    series: [{
      type: 'line',
      xKey: 'week',
      yKey: 'uptime',
      yName: 'Actual Uptime',
      stroke: theme.palette.primary.main,
      strokeWidth: 2,
      marker: {
        fill: theme.palette.primary.main,
        stroke: theme.palette.primary.main,
        size: 6
      },
      tooltip: {
        renderer: (params) => {
          return {
            content: `${params.datum.uptime.toFixed(1)}% uptime`
          };
        }
      }
    }, {
      type: 'line',
      xKey: 'week',
      yKey: 'target',
      yName: 'Target Uptime',
      stroke: theme.palette.grey[400],
      strokeWidth: 1,
      lineDash: [4, 4],
      marker: {
        fill: theme.palette.grey[400],
        stroke: theme.palette.grey[400],
        size: 0
      },
      tooltip: {
        renderer: (params) => {
          return {
            content: `Target: ${params.datum.target}%`
          };
        }
      }
    }],
    axes: [{
      type: 'category',
      position: 'bottom',
      title: {
        text: 'Week'
      }
    }, {
      type: 'number',
      position: 'left',
      title: {
        text: 'Uptime (%)'
      },
      min: 97,
      max: 100,
      tick: {
        values: [97, 98, 99, 100]
      }
    }]
  };

  // Orders Chart Options
  const serviceResolutionChartOptions = {
    title: {
      text: 'Service Ticket Resolution',
    },
    subtitle: {
      text: 'Current distribution of service tickets'
    },
    data: [
      { status: 'Resolved', value: 75, percentage: '75%' },
      { status: 'In Progress', value: 15, percentage: '15%' },
      { status: 'Pending', value: 10, percentage: '10%' }
    ],
    series: [{
      type: 'pie',
      angleKey: 'value',
      sectorLabelKey: 'percentage',
      calloutLabelKey: 'status',
      fill: [
        theme.palette.success.main,
        theme.palette.info.main,
        theme.palette.warning.main
      ],
      tooltip: {
        renderer: (params) => {
          return {
            content: `${params.datum.value}% (${params.datum.status})`
          };
        }
      }
    }]
  };

  const handleRemoveWidget = (widgetId) => {
    setVisibleCharts(prev => prev.filter(id => id !== widgetId));
  };

  const handleAddWidget = (widgetId) => {
    if (!visibleCharts.includes(widgetId)) {
      setVisibleCharts(prev => [...prev, widgetId]);
    }
  };

  const handleReportSelect = async (reportId) => {
    setSelectedReport(reportId);
    try {
      const response = await reportsService.getBusinessInsightsReportById(reportId);
      setSelectedReportData(response.data);
    } catch (error) {
      console.error("Error fetching report details:", error);
    }
  };

  // Add a function to toggle the assistant panel
  const toggleAssistantPanel = () => {
    setVisibleCharts(prev => {
      if (prev.includes('assistant')) {
        return prev.filter(chart => chart !== 'assistant');
      } else {
        return [...prev, 'assistant'];
      }
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3, display: 'flex' }}>
      {/* Main Dashboard Content - adjust width when assistant is visible */}
      <Box sx={{ 
        flexGrow: 1, 
        width: visibleCharts.includes('assistant') ? 'calc(100% - 400px)' : '100%',
        transition: 'width 0.3s ease',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Remove the AppBar and replace with a simple Typography for the page title */}
        <Typography variant="h4" component="div" sx={{ mb: 3 }}>
          Welcome to {appName}
        </Typography>
        
        {/* Add a container for the action buttons that were in the AppBar */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          {/* Only show the GenAI Assistant button if the feature is enabled */}
          {isLLMChatEnabled && (
            <Tooltip title={visibleCharts.includes('assistant') ? "Hide Assistant" : "GenAI Assistant"}>
              <Button
                variant={visibleCharts.includes('assistant') ? "contained" : "outlined"}
                color="primary"
                onClick={toggleAssistantPanel}
                startIcon={<AutoFixHighIcon />}
                sx={{ mr: 2 }}
              >
                {visibleCharts.includes('assistant') ? "Hide Assistant" : "GenAI Assistant"}
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Settings">
            <IconButton>
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <MetricBox 
              title="System Uptime" 
              value="98.7" 
              unit="%" 
              trend="up" 
              trendValue="+0.5% vs last month" 
              icon={SpeedIcon}
              description="Average uptime across all monitored systems"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <MetricBox 
              title="Service Lead Time" 
              value="2.3" 
              unit="days" 
              trend="down" 
              trendValue="-0.7 days vs last month" 
              icon={TimerIcon}
              description="Average time from service request to completion"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <MetricBox 
              title="Active Service Events" 
              value="24" 
              trend="up" 
              trendValue="+3 events vs last week" 
              icon={EventIcon}
              description="Currently open service tickets"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <MetricBox 
              title="First-Time Fix Rate" 
              value="92" 
              unit="%" 
              trend="up" 
              trendValue="+3% vs last quarter" 
              icon={CheckCircle}
              description="Percentage of issues resolved on first visit"
            />
          </Grid>
        </Grid>

        <Box sx={{ flexGrow: 1 }}>
          <ResponsiveGridLayout
            className="layout"
            layouts={layouts}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
            rowHeight={30}
            onLayoutChange={(layout, layouts) => setLayouts(layouts)}
            isDraggable={true}
            isResizable={true}
            margin={[16, 16]}
            containerPadding={[16, 16]}
            autoSize={true}
          >
            {visibleCharts.includes('serviceResponse') && (
              <Paper key="serviceResponse" elevation={2} sx={{ height: '100%', position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: 5, right: 5, zIndex: 10 }}>
                  <IconButton size="small" sx={{ mr: 1 }}>
                    <DragIndicatorIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleRemoveWidget('serviceResponse')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ height: '100%', p: 2 }}>
                  <AgCharts options={serviceResponseChartOptions} />
                </Box>
              </Paper>
            )}

            {visibleCharts.includes('equipmentHealth') && (
              <Paper key="equipmentHealth" elevation={2} sx={{ height: '100%', position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: 5, right: 5, zIndex: 10 }}>
                  <IconButton size="small" sx={{ mr: 1 }}>
                    <DragIndicatorIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleRemoveWidget('equipmentHealth')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ height: '100%', p: 2 }}>
                  <AgCharts options={equipmentHealthChartOptions} />
                </Box>
              </Paper>
            )}

            {visibleCharts.includes('weeklyServiceUptime') && (
              <Paper key="weeklyServiceUptime" elevation={2} sx={{ height: '100%', position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: 5, right: 5, zIndex: 10 }}>
                  <IconButton size="small" sx={{ mr: 1 }}>
                    <DragIndicatorIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleRemoveWidget('weeklyServiceUptime')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ height: '100%', p: 2 }}>
                  <AgCharts options={weeklyServiceUptimeChartOptions} />
                </Box>
              </Paper>
            )}

            {visibleCharts.includes('serviceResolution') && (
              <Paper key="serviceResolution" elevation={2} sx={{ height: '100%', position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: 5, right: 5, zIndex: 10 }}>
                  <IconButton size="small" sx={{ mr: 1 }}>
                    <DragIndicatorIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleRemoveWidget('serviceResolution')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ height: '100%', p: 2 }}>
                  <AgCharts options={serviceResolutionChartOptions} />
                </Box>
              </Paper>
            )}
          </ResponsiveGridLayout>
        </Box>

        <NotificationTicker 
          notifications={[
            { id: 1, type: 'warning', message: 'Low inventory alert for SKU #12345 - Reorder recommended' },
            { id: 2, type: 'info', message: 'Order #89012 has shipped and is expected to arrive on June 15' },
            { id: 3, type: 'error', message: 'Delivery delay for Order #45678 due to weather conditions' }
          ]}
          sx={{ mt: 3 }}
        />
      </Box>

      {/* Business Intelligence Assistant Side Panel - only render if feature is enabled and visible */}
      {isLLMChatEnabled && visibleCharts.includes('assistant') && (
        <Paper 
          elevation={3} 
          sx={{ 
            width: 600, 
            height: 'calc(100vh - 100px)',
            position: 'fixed', 
            right: 24, 
            top: 80,
            display: 'flex', 
            flexDirection: 'column',
            zIndex: 1000,
            transition: 'transform 0.3s ease',
            transform: visibleCharts.includes('assistant') ? 'translateX(0)' : 'translateX(400px)'
          }}
        >
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <AutoFixHighIcon sx={{ mr: 1 }} />
              Business Insights Agent
            </Typography>
            <IconButton size="small" onClick={() => handleRemoveWidget('assistant')}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          
          {/* Display the hardcoded report summary */}
          {selectedReportData && (
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', maxHeight: '30%', overflow: 'auto' }}>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                {selectedReportData.title}
              </Typography>
              <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-line' }}>
                {selectedReportData.summary}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Data period: {new Date(selectedReportData.data_period_start).toLocaleDateString()} - {new Date(selectedReportData.data_period_end).toLocaleDateString()} Created by GenAI Agent
                </Typography>
              </Box>
            </Box>
          )}
          
          {/* Loading indicator while fetching report */}
          {loading && !selectedReportData && (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          )}
          
          {/* Conversation Panel */}
          <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <ConversationPanel 
              reportId={selectedReport} 
              userId="current-user"
              reportData={selectedReportData}
            />
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default Dashboard; 